import { zodResolver } from "@hookform/resolvers/zod";
import { ErrorOutline, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import i18n from "../../../config/i18n";
import { formatApiError } from "../../../utils/formatApiError";
import {
  getNationalLengthForCountry,
  requiresStrictNationalLength,
  sanitizePhoneDigits,
  toE164,
  trimPhoneToNationalLength,
} from "../../../utils/CountryPhoneUtils";
import { type Country } from "../../../utils/countries";
import CountryCodeSelect from "../../common/CountryCodeSelect";
import OtpInput from "./OtpInput";

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

const getPhonePasswordSchema = () => {
  const t = (key: string) => i18n.t(key);
  return z
    .object({
      countryCode: z.string().min(2).max(3),
      phoneNumber: z.string(),
      password: z.string().min(1, t("validation.passwordRequired")),
    })
    .superRefine((data, ctx) => {
      if (!requiresStrictNationalLength(data.countryCode)) return;
      const digits = sanitizePhoneDigits(data.phoneNumber);
      if (!digits) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation.phoneRequired"),
          path: ["phoneNumber"],
        });
        return;
      }
      const expected = getNationalLengthForCountry(data.countryCode);
      if (digits.length !== expected) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation.phoneInvalid"),
          path: ["phoneNumber"],
        });
      }
    });
};

const getPhoneOtpSendSchema = () => {
  const t = (key: string) => i18n.t(key);
  return z
    .object({
      countryCode: z.string().min(2).max(3),
      phoneNumber: z.string(),
    })
    .superRefine((data, ctx) => {
      if (!requiresStrictNationalLength(data.countryCode)) return;
      const digits = sanitizePhoneDigits(data.phoneNumber);
      if (!digits) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation.phoneRequired"),
          path: ["phoneNumber"],
        });
        return;
      }
      const expected = getNationalLengthForCountry(data.countryCode);
      if (digits.length !== expected) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation.phoneInvalid"),
          path: ["phoneNumber"],
        });
      }
    });
};

type PhonePasswordData = z.infer<ReturnType<typeof getPhonePasswordSchema>>;
type PhoneOtpSendData = z.infer<ReturnType<typeof getPhoneOtpSendSchema>>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PhoneLoginFormProps {
  onLogin: (data: { phoneNumber: string; password: string }) => Promise<void>;
  onOtpLogin: (data: { phoneNumber: string; otp: string }) => Promise<void>;
  onSendOtp: (phone: string, countryCode: string) => Promise<void>;
  onForgotPassword?: () => void;
  isLoading?: boolean;
  error?: string | null;
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

const ErrorHelperText: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) return null;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        mt: 0.5,
        color: "error.main",
      }}
    >
      <ErrorOutline sx={{ fontSize: 16 }} />
      <Typography variant="caption" sx={{ color: "error.main" }}>
        {message}
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type OtpPhase = "idle" | "sent";

const PhoneLoginForm: React.FC<PhoneLoginFormProps> = ({
  onLogin,
  onOtpLogin,
  onSendOtp,
  onForgotPassword,
  isLoading = false,
  error = null,
}) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [useOtpMode, setUseOtpMode] = useState(false);
  const [otpPhase, setOtpPhase] = useState<OtpPhase>("idle");
  const [e164Phone, setE164Phone] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [sendOtpError, setSendOtpError] = useState<string | null>(null);
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  // --- Password login form ---
  const {
    control: pwControl,
    handleSubmit: handlePwSubmit,
    watch: watchPw,
    setValue: setPwValue,
    formState: { errors: pwErrors, touchedFields: pwTouched, isSubmitted: pwIsSubmitted },
  } = useForm<PhonePasswordData>({
    resolver: zodResolver(getPhonePasswordSchema()),
    defaultValues: { countryCode: "TJ", phoneNumber: "", password: "" },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // --- OTP send form ---
  const {
    control: otpControl,
    handleSubmit: handleOtpSendSubmit,
    watch: watchOtp,
    setValue: setOtpValue,
    formState: {
      errors: otpErrors,
      touchedFields: otpTouched,
      isSubmitted: otpIsSubmitted,
    },
  } = useForm<PhoneOtpSendData>({
    resolver: zodResolver(getPhoneOtpSendSchema()),
    defaultValues: { countryCode: "TJ", phoneNumber: "" },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const pwCountryCode = watchPw("countryCode");
  const pwMaxLen = getNationalLengthForCountry(pwCountryCode);
  const pwPhoneErrorVisible =
    !!pwErrors.phoneNumber && (pwTouched.phoneNumber || pwIsSubmitted);

  const otpCountryCode = watchOtp("countryCode");
  const otpMaxLen = getNationalLengthForCountry(otpCountryCode);
  const otpPhoneErrorVisible =
    !!otpErrors.phoneNumber && (otpTouched.phoneNumber || otpIsSubmitted);

  const handlePwCountrySelect = (country: Country | null) => {
    const code = country?.code ?? "TJ";
    setPwValue("countryCode", code, { shouldDirty: true, shouldValidate: false });
    const trimmed = trimPhoneToNationalLength(code, watchPw("phoneNumber") || "");
    setPwValue("phoneNumber", trimmed, { shouldDirty: true, shouldValidate: false });
  };

  const handleOtpCountrySelect = (country: Country | null) => {
    const code = country?.code ?? "TJ";
    setOtpValue("countryCode", code, { shouldDirty: true, shouldValidate: false });
    const trimmed = trimPhoneToNationalLength(code, watchOtp("phoneNumber") || "");
    setOtpValue("phoneNumber", trimmed, { shouldDirty: true, shouldValidate: false });
  };

  // Password submit
  const handlePasswordSubmit = async (data: PhonePasswordData) => {
    const phone = toE164(data.countryCode, data.phoneNumber);
    await onLogin({ phoneNumber: phone, password: data.password });
  };

  // Send OTP
  const handleSendOtp = async (data: PhoneOtpSendData) => {
    const phone = toE164(data.countryCode, data.phoneNumber);
    setE164Phone(phone);
    setSendOtpError(null);
    setIsSendingOtp(true);
    try {
      await onSendOtp(phone, data.countryCode);
      setOtpPhase("sent");
    } catch (err: unknown) {
      setSendOtpError(formatApiError(err));
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    setSendOtpError(null);
    setIsSendingOtp(true);
    try {
      await onSendOtp(e164Phone, otpCountryCode);
    } catch (err: unknown) {
      setSendOtpError(formatApiError(err));
    } finally {
      setIsSendingOtp(false);
    }
  };

  // OTP complete — auto-submit
  const handleOtpComplete = async (code: string) => {
    setOtpError(null);
    try {
      await onOtpLogin({ phoneNumber: e164Phone, otp: code });
    } catch {
      setOtpError(t("auth.invalidOtp"));
    }
  };

  const handleSwitchToOtp = () => {
    setUseOtpMode(true);
    setOtpPhase("idle");
    setOtpError(null);
    setSendOtpError(null);
  };

  const handleSwitchToPassword = () => {
    setUseOtpMode(false);
    setOtpPhase("idle");
    setOtpError(null);
    setSendOtpError(null);
  };

  // ------------------------------------------------------------------ //
  // Password mode
  // ------------------------------------------------------------------ //
  if (!useOtpMode) {
    return (
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handlePwSubmit(handlePasswordSubmit)();
        }}
        sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}
        noValidate
      >
        {/* Phone */}
        <Controller
          name="phoneNumber"
          control={pwControl}
          render={({ field }) => (
            <Box>
              <TextField
                {...field}
                label={t("auth.phoneNumber")}
                className="notranslate"
                translate="no"
                fullWidth
                required
                autoComplete="tel-national"
                error={pwPhoneErrorVisible}
                disabled={isLoading}
                onChange={(e) => {
                  const trimmed = trimPhoneToNationalLength(
                    pwCountryCode,
                    e.target.value || ""
                  );
                  field.onChange(trimmed);
                }}
                onBlur={field.onBlur}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pr: 1, borderRight: "1px solid #ccc", mr: 1 }}>
                        <img
                          loading="lazy"
                          width={20}
                          height={14}
                          src="https://flagcdn.com/w20/tj.png"
                          srcSet="https://flagcdn.com/w40/tj.png 2x"
                          alt="Tajikistan"
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>+992</Typography>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: pwMaxLen,
                }}
              />
              <ErrorHelperText
                message={
                  pwPhoneErrorVisible ? pwErrors.phoneNumber?.message : undefined
                }
              />
              {!pwPhoneErrorVisible && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, mt: 0.5, display: "block" }}>
                  {t("auth.tajikistanOnlyHint")}
                </Typography>
              )}
            </Box>
          )}
        />

        {/* Password */}
        <Controller
          name="password"
          control={pwControl}
          render={({ field }) => (
            <Box>
              <TextField
                {...field}
                label={t("auth.password")}
                className="notranslate"
                translate="no"
                fullWidth
                required
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                error={!!pwErrors.password}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        disabled={isLoading}
                        aria-label={
                          showPassword
                            ? t("auth.hidePassword")
                            : t("auth.showPassword")
                        }
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <ErrorHelperText message={pwErrors.password?.message} />
            </Box>
          )}
        />

        {/* Forgot password + OTP link row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {onForgotPassword && (
            <Link
              component="button"
              type="button"
              className="notranslate"
              translate="no"
              onClick={onForgotPassword}
              disabled={isLoading}
              sx={{ fontSize: "0.875rem" }}
            >
              {t("auth.forgotPassword")}
            </Link>
          )}
          <Link
            component="button"
            type="button"
            className="notranslate"
            translate="no"
            onClick={handleSwitchToOtp}
            disabled={isLoading}
            sx={{ fontSize: "0.875rem" }}
          >
            {t("auth.loginWithOtp")}
          </Link>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          className="notranslate"
          translate="no"
          disabled={isLoading}
          sx={{ fontWeight: 600, py: 1.2 }}
        >
          {isLoading ? (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} color="inherit" />
              <span>{t("auth.signIn")}</span>
            </Box>
          ) : (
            t("auth.signIn")
          )}
        </Button>
      </Box>
    );
  }

  // ------------------------------------------------------------------ //
  // OTP mode — phone input phase
  // ------------------------------------------------------------------ //
  if (useOtpMode && otpPhase === "idle") {
    return (
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleOtpSendSubmit(handleSendOtp)();
        }}
        sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}
        noValidate
      >
        <Typography
          variant="body2"
          sx={{ color: "text.secondary" }}
          className="notranslate"
          translate="no"
        >
          {t("auth.enterPhoneForOtp")}
        </Typography>

        <Controller
          name="phoneNumber"
          control={otpControl}
          render={({ field }) => (
            <Box>
              <TextField
                {...field}
                label={t("auth.phoneNumber")}
                className="notranslate"
                translate="no"
                fullWidth
                required
                autoComplete="tel-national"
                error={otpPhoneErrorVisible}
                disabled={isSendingOtp || isLoading}
                value={field.value ?? ""}
                onChange={(e) => {
                  const trimmed = trimPhoneToNationalLength(
                    otpCountryCode,
                    e.target.value || ""
                  );
                  setOtpValue("phoneNumber", trimmed, { shouldDirty: true, shouldValidate: false });
                }}
                onBlur={field.onBlur}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pr: 1, borderRight: "1px solid #ccc", mr: 1 }}>
                        <img
                          loading="lazy"
                          width={20}
                          height={14}
                          src="https://flagcdn.com/w20/tj.png"
                          srcSet="https://flagcdn.com/w40/tj.png 2x"
                          alt="Tajikistan"
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>+992</Typography>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: otpMaxLen,
                }}
              />
              <ErrorHelperText
                message={
                  otpPhoneErrorVisible
                    ? otpErrors.phoneNumber?.message
                    : undefined
                }
              />
              {!otpPhoneErrorVisible && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, mt: 0.5, display: "block" }}>
                  {t("auth.tajikistanOnlyHint")}
                </Typography>
              )}
            </Box>
          )}
        />

        {(sendOtpError || error) && (
          <Alert severity="error">{sendOtpError || error}</Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          className="notranslate"
          translate="no"
          disabled={isSendingOtp || isLoading}
          sx={{ fontWeight: 600, py: 1.2 }}
        >
          {isSendingOtp ? (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} color="inherit" />
              <span>{t("auth.sendingCode")}</span>
            </Box>
          ) : (
            t("auth.sendOtp")
          )}
        </Button>

        <Link
          component="button"
          type="button"
          className="notranslate"
          translate="no"
          onClick={handleSwitchToPassword}
          disabled={isLoading}
          sx={{ fontSize: "0.875rem", textAlign: "center" }}
        >
          {t("auth.loginWithPassword")}
        </Link>
      </Box>
    );
  }

  // ------------------------------------------------------------------ //
  // OTP mode — OTP entry phase
  // ------------------------------------------------------------------ //
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
        alignItems: "center",
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", textAlign: "center" }}
        className="notranslate"
        translate="no"
      >
        {t("auth.codeSentTo", { phone: e164Phone })}
      </Typography>

      <OtpInput
        onComplete={handleOtpComplete}
        onResend={handleResendOtp}
        error={otpError || error}
        isLoading={isLoading || isSendingOtp}
      />

      <Link
        component="button"
        type="button"
        className="notranslate"
        translate="no"
        onClick={() => setOtpPhase("idle")}
        disabled={isLoading}
        sx={{ fontSize: "0.875rem" }}
      >
        {t("auth.back")}
      </Link>
    </Box>
  );
};

export default PhoneLoginForm;
